/**
 * The app rail, drawn as finixt-ui's AppRail with slots the package's own
 * does not have.
 *
 * AppRail's header is its ModuleSwitcher and its footer its AccountSwitcher,
 * both on finixt-ui's keyboard-less menu, and there is no slot to put a local
 * one in their place. This aside draws the same surface, border, rhythm, and
 * search row, and leaves the header and footer to the caller. Collapse state
 * comes from ShellContext, which AppLayout publishes, so the rail is right at
 * any nesting depth; the toggle lives in the header, as finixt-web places it.
 *
 * `collapsed` is cloned onto direct children, as AppRail does, so finixt-ui's
 * NavSection draws its hairline when the rail is glyphs only. Children must
 * therefore be a flat list, not wrapped in a fragment.
 */
import { Children, cloneElement, isValidElement, useContext, type ReactNode } from 'react';

import { Icon, IconButton, Kbd, ShellContext } from './finixt.js';

const SEARCH_LABEL = 'Jump to…';

export interface RailProps {
  /** The workspace identity: the tenant switcher and the way back out. */
  header?: ReactNode;
  /** The account row. Pinned to the bottom. */
  footer?: ReactNode;
  /** Opens the command palette. The search row is drawn only when given. */
  onSearch?: (() => void) | undefined;
  /** NavSection and NavLink rows. */
  children?: ReactNode;
}

/** The search row: a button drawn as finixt-ui's search Input. */
function SearchRow({ collapsed, onClick }: { collapsed: boolean; onClick: () => void }) {
  if (collapsed) {
    return (
      <IconButton icon="search" size={34} tone="bare" onClick={onClick} aria-label={SEARCH_LABEL} />
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        boxSizing: 'border-box',
        padding: '7px 44px 7px 34px',
        border: '1px solid var(--border-hairline)',
        borderRadius: 'var(--radius-input)',
        background: 'var(--surface-active)',
        color: 'var(--text-faint)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-body-sm)',
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      <Icon
        name="search"
        size={17}
        color="var(--text-faint)"
        style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }}
      />
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {SEARCH_LABEL}
      </span>
      <Kbd style={{ position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)' }}>
        ⌘K
      </Kbd>
    </button>
  );
}

/** The aside. */
export function Rail({ header, footer, onSearch, children }: RailProps) {
  const shell = useContext(ShellContext);
  const collapsed = Boolean(shell.railCollapsed);
  return (
    <aside
      style={{
        width: '100%',
        flexShrink: 0,
        background: 'var(--surface-card)',
        borderRight: '1px solid var(--border-hairline)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        boxSizing: 'border-box',
      }}
    >
      {header ? (
        <div
          style={{
            padding: collapsed ? '11px 0' : '11px 11px 5px',
            display: 'flex',
            flexDirection: 'column',
            gap: 5,
            alignItems: collapsed ? 'center' : 'stretch',
          }}
        >
          {header}
        </div>
      ) : null}

      {onSearch ? (
        <div
          style={{
            padding: collapsed ? '0 11px' : '2px 11px 11px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <SearchRow collapsed={collapsed} onClick={onSearch} />
        </div>
      ) : null}

      <nav
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 5,
          padding: collapsed ? '0 11px' : '2px 11px 11px',
        }}
      >
        {Children.map(children, (child) =>
          isValidElement<{ collapsed?: boolean | undefined }>(child)
            ? cloneElement(child, { collapsed })
            : child,
        )}
      </nav>

      {/* A column that stretches, as the header is: a menu trigger sits in a
          span that hugs its contents, so in a row a full-width trigger grows
          to its text instead of stopping at the rail's edge. */}
      {footer ? (
        <div
          style={{
            flexShrink: 0,
            minHeight: 'var(--h-header)',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: collapsed ? 'center' : 'stretch',
            padding: collapsed ? '9px 0' : '9px 11px',
          }}
        >
          {footer}
        </div>
      ) : null}
    </aside>
  );
}
